import { useRef, useEffect } from 'react';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { reorderWithEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/reorder-with-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';
import { unsafeOverflowAutoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/unsafe-overflow/element';
import invariant from 'tiny-invariant';
import { bindAll } from 'bind-event-listener';

import {
  isCardData,
  isCardDropTargetData,
  isColumnData,
  isDraggingACard,
  isDraggingAColumn,
} from '../components/boardColumn/data';
import { blockBoardPanningAttr } from '../components/boardColumn/dataAttributes';

export const useBoard = ({
  settings,
  data,
  setData,
  onMoveCardToAnotherColumn,
  onMoveColumn,
  disableVerticalDragging,
}) => {
  const scrollableRef = useRef(null);

  useEffect(() => {
    const element = scrollableRef.current;
    invariant(element);
    return combine(
      monitorForElements({
        canMonitor: isDraggingACard,
        onDrop({ source, location }) {
          const dragging = source.data;
          if (!isCardData(dragging)) {
            return;
          }

          const innerMost = location.current.dropTargets[0];

          if (!innerMost) {
            return;
          }
          const dropTargetData = innerMost.data;
          const homeColumnIndex = data.columns.findIndex((column) => column.id === dragging.columnId);
          const home = data.columns[homeColumnIndex];

          if (!home) {
            return;
          }

          // dropping on a card
          if (isCardDropTargetData(dropTargetData)) {
            const destinationColumnIndex = data.columns.findIndex((column) => column.id === dropTargetData.columnId);
            const destination = data.columns[destinationColumnIndex];
            // reordering in home column
            if (home === destination) {
              // Skip vertical reordering if disableVerticalDragging is true
              if (disableVerticalDragging) {
                return;
              }

              const cardFinishIndex = home.cards.findIndex((card) => card.id === dropTargetData.card.id);
              const closestEdge = extractClosestEdge(dropTargetData);

              const reordered = reorderWithEdge({
                axis: 'vertical',
                list: home.cards,
                // startIndex: cardIndexInHome,
                indexOfTarget: cardFinishIndex,
                closestEdgeOfTarget: closestEdge,
              });

              const updated = {
                ...home,
                cards: reordered,
              };
              const newColumns = Array.from(data.columns);
              newColumns[homeColumnIndex] = updated;
              setData({ ...data, columns: newColumns });
              return;
            }

            // moving card from one column to another

            // unable to find destination
            if (!destination) {
              return;
            }

            onMoveCardToAnotherColumn(dragging, dropTargetData);
            return;
          }

          // dropping onto a column, but not onto a card
          if (isColumnData(dropTargetData)) {
            const destinationColumnIndex = data.columns.findIndex((column) => column.id === dropTargetData.column.id);
            const destination = data.columns[destinationColumnIndex];

            if (!destination) {
              return;
            }

            // dropping on home
            if (home === destination) {
              // Skip vertical reordering if disableVerticalDragging is true
              if (disableVerticalDragging) {
                return;
              }

              // move to last position
              const reordered = reorder({
                list: home.cards,
                // startIndex: cardIndexInHome,
                finishIndex: home.cards.length - 1,
              });

              const updated = {
                ...home,
                cards: reordered,
              };
              const newColumns = Array.from(data.columns);
              newColumns[homeColumnIndex] = updated;
              setData({ ...data, columns: newColumns });
              return;
            }

            onMoveCardToAnotherColumn(dragging, dropTargetData);
          }
        },
      }),
      monitorForElements({
        canMonitor: isDraggingAColumn,
        onDrop({ source, location }) {
          const dragging = source.data;
          if (!isColumnData(dragging)) {
            return;
          }

          // Prevent dragging the unassigned column
          if (dragging.column.id === 'column:unassigned') {
            return;
          }

          const innerMost = location.current.dropTargets[0];

          if (!innerMost) {
            return;
          }
          const dropTargetData = innerMost.data;

          if (!isColumnData(dropTargetData)) {
            return;
          }

          // Prevent dropping on the unassigned column
          if (dropTargetData.column.id === 'column:unassigned') {
            return;
          }

          const homeIndex = data.columns.findIndex((column) => column.id === dragging.column.id);
          const destinationIndex = data.columns.findIndex((column) => column.id === dropTargetData.column.id);

          if (homeIndex === -1 || destinationIndex === -1) {
            return;
          }

          if (homeIndex === destinationIndex) {
            return;
          }

          const reordered = reorder({
            list: data.columns,
            startIndex: homeIndex,
            finishIndex: destinationIndex,
          });
          onMoveColumn(reordered.filter((column) => column.id !== 'column:unassigned'));
        },
      }),
      autoScrollForElements({
        canScroll({ source }) {
          if (!settings.isOverElementAutoScrollEnabled) {
            return false;
          }

          return isDraggingACard({ source }) || isDraggingAColumn({ source });
        },
        getConfiguration: () => ({ maxScrollSpeed: settings.boardScrollSpeed }),
        element,
      }),
      unsafeOverflowAutoScrollForElements({
        element,
        getConfiguration: () => ({ maxScrollSpeed: settings.boardScrollSpeed }),
        canScroll({ source }) {
          if (!settings.isOverElementAutoScrollEnabled) {
            return false;
          }

          if (!settings.isOverflowScrollingEnabled) {
            return false;
          }

          return isDraggingACard({ source }) || isDraggingAColumn({ source });
        },
        getOverflow() {
          return {
            forLeftEdge: {
              top: 1000,
              left: 1000,
              bottom: 1000,
            },
            forRightEdge: {
              top: 1000,
              right: 1000,
              bottom: 1000,
            },
          };
        },
      }),
    );
  }, [data, onMoveCardToAnotherColumn, onMoveColumn, settings, disableVerticalDragging, setData]);

  // Panning the board
  useEffect(() => {
    let cleanupActive = null;
    const scrollable = scrollableRef.current;
    invariant(scrollable);

    function begin({ startX }) {
      let lastX = startX;

      const cleanupEvents = bindAll(
        window,
        [
          {
            type: 'pointermove',
            listener(event) {
              const currentX = event.clientX;
              const diffX = lastX - currentX;

              lastX = currentX;
              scrollable?.scrollBy({ left: diffX });
            },
          },
          // stop panning if we see any of these events
          ...['pointercancel', 'pointerup', 'pointerdown', 'keydown', 'resize', 'click', 'visibilitychange'].map(
            (eventName) => ({ type: eventName, listener: () => cleanupEvents() }),
          ),
        ],
        // need to make sure we are not after the "pointerdown" on the scrollable
        // Also this is helpful to make sure we always hear about events from this point
        { capture: true },
      );

      cleanupActive = cleanupEvents;
    }

    const cleanupStart = bindAll(scrollable, [
      {
        type: 'pointerdown',
        listener(event) {
          if (!(event.target instanceof HTMLElement)) {
            return;
          }
          // ignore interactive elements
          if (event.target.closest(`[${blockBoardPanningAttr}]`)) {
            return;
          }

          begin({ startX: event.clientX });
        },
      },
    ]);

    return function cleanupAll() {
      cleanupStart();
      cleanupActive?.();
    };
  }, []);

  return { scrollableRef };
};
